import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { extendRecurrenceApi, listRecurrencesApi, updateRecurrenceApi } from "../api/financeiroApi";
import { ENTRY_TYPE_LABELS, FinRecurrence, FREQUENCY_LABELS } from "../api/finTypes";
import { FIN_ENTRY_NEW_PAGE } from "../routes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { parseMoneyInput } from "../utils/fin";
import { useCostCenters } from "../utils/costCenters";
import CostCenterCascade from "./CostCenterCascade";
import AllocationEditor, { AllocationRow, allocationErrors, allocationPayload } from "./AllocationEditor";

const toInput = (amount: string) => Number(amount).toFixed(2).replace(".", ",");
const sameAllocations = (rows: AllocationRow[], saved: FinRecurrence["allocations"]) =>
  JSON.stringify(allocationPayload(rows)) === JSON.stringify(saved.map((a) => ({ costCenterId: a.costCenterId, amount: Number(a.amount) })));

type Props = { token: string; canWrite: boolean };

type Confirm = { kind: "stop" | "extend"; recurrence: FinRecurrence } | null;

/**
 * Repetições: a regra que gerou os lançamentos. Mudar valor ou descrição alcança
 * só os lançamentos futuros ainda em aberto; encerrar remove esses mesmos.
 */
export default function RecurrencesSettings({ token, canWrite }: Props) {
  const navigate = useNavigate();
  const [list, setList] = useState<FinRecurrence[] | null>(null);
  const [editing, setEditing] = useState<FinRecurrence | null>(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [endDate, setEndDate] = useState("");
  const [costCenterId, setCostCenterId] = useState("");
  const [allocating, setAllocating] = useState(false);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const { costCenters, groups } = useCostCenters(token);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listRecurrencesApi(token)
      .then(setList)
      .catch((error: Error) => toast.error(error.message));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (r: FinRecurrence) => {
    setEditing(r);
    setDescription(r.description);
    setAmount(r.amount);
    setEndDate(r.endDate ?? "");
    setCostCenterId(r.costCenterId ?? "");
    setAllocating(r.allocations.length > 0);
    setAllocations(r.allocations.map((a) => ({ costCenterId: a.costCenterId, amount: toInput(a.amount) })));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const value = parseMoneyInput(amount);
    if (description.trim().length < 2) return toast.error("Informe a descrição.");
    if (!(value > 0)) return toast.error("Informe o valor.");
    const classError = allocating ? allocationErrors(allocations, value)[0] : costCenterId ? null : "Informe o centro de custo.";
    if (classError) return toast.error(classError);
    // Classificação só vai quando mudou: senão a regra regravaria a de cada lançamento futuro.
    const classification = allocating
      ? sameAllocations(allocations, editing.allocations)
        ? {}
        : { costCenterId: null, allocations: allocationPayload(allocations) }
      : costCenterId === editing.costCenterId && !editing.allocations.length
        ? {}
        : { costCenterId, allocations: null };

    setBusy(true);
    try {
      const result = await updateRecurrenceApi(token, editing.id, {
        description: description.trim(),
        amount: value,
        endDate: endDate || null,
        ...classification,
      });
      toast.success(
        `Repetição salva: ${result.updatedEntries} lançamento(s) futuro(s) atualizado(s)` +
          (result.removedEntries ? `, ${result.removedEntries} removido(s).` : ".")
      );
      setEditing(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a repetição.");
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === "extend") {
        const result = await extendRecurrenceApi(token, confirm.recurrence.id);
        toast.success(`${result.generated} lançamento(s) gerado(s), até ${formatDateOnly(result.generatedUntil)}.`);
      } else {
        const result = await updateRecurrenceApi(token, confirm.recurrence.id, { isActive: false });
        toast.success(`Repetição encerrada: ${result.removedEntries} lançamento(s) futuro(s) removido(s).`);
      }
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a repetição.");
    } finally {
      setConfirm(null);
      setBusy(false);
    }
  };

  return (
    <div className="dpeSection">
      <div className="dpControls">
        <p className="finHint">
          Cada repetição gera os lançamentos até 12 meses à frente. Perto do fim, use "Estender".
        </p>
        {canWrite ? (
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(`${FIN_ENTRY_NEW_PAGE("PAGAR")}&forma=repetir`)}>
            Nova repetição
          </button>
        ) : null}
      </div>

      <section className="ecoCard">
        <div className="ecoTableWrap">
          <table className="ecoTable finTable">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Tipo</th>
                <th>Frequência</th>
                <th className="ecoTable__num">Valor</th>
                <th>Gerado até</th>
                {canWrite ? <th className="ecoTable__num">Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {!list ? (
                <tr>
                  <td colSpan={6} className="ecoEmpty">
                    Carregando...
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={6} className="ecoEmpty">
                    Nenhuma repetição. Crie em "Nova conta a pagar", forma "Repetir".
                  </td>
                </tr>
              ) : (
                list.map((r) => (
                  <tr key={r.id} className={r.isActive ? "" : "finRow--canceled"}>
                    <td>
                      <span className="finDesc">
                        <span className="finDesc__main">{r.description}</span>
                        <span className="finDesc__sub">
                          {[r.counterparty?.name, r.category?.name].filter(Boolean).join(" · ")}
                          {!r.isActive ? " · Encerrada" : ""}
                        </span>
                      </span>
                    </td>
                    <td>{ENTRY_TYPE_LABELS[r.type]}</td>
                    <td>
                      {FREQUENCY_LABELS[r.frequency]}
                      <div className="finDesc__sub">
                        desde {formatDateOnly(r.startDate)}
                        {r.endDate ? ` até ${formatDateOnly(r.endDate)}` : ""}
                      </div>
                    </td>
                    <td className="ecoTable__num finMoney">{formatCurrency(r.amount)}</td>
                    <td>{formatDateOnly(r.generatedUntil)}</td>
                    {canWrite ? (
                      <td className="ecoTable__num">
                        {r.isActive ? (
                          <div className="dpTable__actions" style={{ justifyContent: "flex-end" }}>
                            <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => openEdit(r)}>
                              Editar
                            </button>
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm"
                              onClick={() => setConfirm({ kind: "extend", recurrence: r })}
                            >
                              Estender
                            </button>
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm ecoBtn--danger"
                              onClick={() => setConfirm({ kind: "stop", recurrence: r })}
                            >
                              Encerrar
                            </button>
                          </div>
                        ) : (
                          <span className="ecoTable__faint">—</span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Editar repetição">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Editar repetição</h2>
              <button
                type="button"
                className="dpModal__close"
                onClick={() => setEditing(null)}
                disabled={busy}
                aria-label="Fechar"
              >
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <p className="dpNotice" style={{ margin: 0 }}>
                  As mudanças valem para os lançamentos com vencimento a partir de hoje que ainda não têm baixa. Os
                  passados e os já pagos ficam como estão.
                </p>
                <div className="dpFormGroup">
                  <label htmlFor="rcDesc">Descrição *</label>
                  <input
                    id="rcDesc"
                    className="dpInput"
                    maxLength={200}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="rcAmount">Valor (R$) *</label>
                    <input
                      id="rcAmount"
                      className="dpInput"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="rcEnd">Repetir até</label>
                    <input
                      id="rcEnd"
                      type="date"
                      className="dpInput"
                      value={endDate}
                      min={editing.startDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={busy}
                    />
                    <span className="dpFormHint">Encurtar remove os lançamentos em aberto depois da data.</span>
                  </div>
                </div>
                {allocating ? (
                  <div className="dpFormGroup">
                    <div className="finAlloc__head">
                      <span className="finAlloc__title">Rateio entre centros de custo *</span>
                      <button type="button" className="finLinkBtn" onClick={() => setAllocating(false)} disabled={busy}>
                        Usar um centro só
                      </button>
                    </div>
                    <AllocationEditor
                      rows={allocations}
                      onChange={setAllocations}
                      total={parseMoneyInput(amount)}
                      costCenters={costCenters}
                      groups={groups}
                      disabled={busy}
                    />
                  </div>
                ) : (
                  <div className="dpFormGrid">
                  <CostCenterCascade
                    idPrefix="rc"
                    value={costCenterId}
                    onChange={setCostCenterId}
                    costCenters={costCenters}
                    groups={groups}
                    disabled={busy}
                    footer={
                    <button
                      type="button"
                      className="finLinkBtn"
                      onClick={() => {
                        setAllocating(true);
                        if (allocations.length < 2) {
                          setAllocations([
                            { costCenterId, amount: "" },
                            { costCenterId: "", amount: "" },
                          ]);
                        }
                      }}
                      disabled={busy}
                    >
                      Ratear entre centros
                    </button>
                    }
                  />
                  </div>
                )}
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setEditing(null)} disabled={busy}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={busy}>
                  {busy ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === "extend" ? "Estender repetição?" : "Encerrar repetição?"}
        message={
          confirm?.kind === "extend"
            ? `Gera mais 12 meses de "${confirm.recurrence.description}" depois de ${formatDateOnly(confirm.recurrence.generatedUntil)}.`
            : `Os lançamentos futuros em aberto de "${confirm?.recurrence.description ?? ""}" serão removidos. Os passados e os já pagos ficam.`
        }
        confirmLabel={confirm?.kind === "extend" ? "Estender" : "Encerrar"}
        tone={confirm?.kind === "stop" ? "danger" : "default"}
        busy={busy}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
